// Change a numbered turnover's job folder (2026-09-28) — opened from the ✏️ on
// the header's Turnover folder strip. Pick an existing folder from the prime
// entity's <year> Jobs folder (the job's own folders first) or paste a
// SharePoint folder link. Saving makes sure <folder>/Turnover and its numbered
// section subfolders exist (never overwriting anything), then stores the
// folder's link + item id (sp_SetTurnoverFolder) — uploads then anchor to that
// folder whatever it is named.
import { useEffect, useState } from "react";
import { turnoverWrite, payloadOf } from "../../utils/sqlClient";
import { listJobFolders, resolveFolderLink, ensureTurnoverSubfolders, entityBasePath } from "../../utils/sharePoint";
import { rankJobFolders, setFolderPayload } from "./folderPath";
import { shareIdForUrl } from "./packageData";

const btn = (primary) => ({ padding: "7px 16px", fontSize: 13, fontWeight: 700, borderRadius: 6, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--cs-border)", background: primary ? "#1e3a5f" : "var(--surface)", color: primary ? "#fff" : "var(--text)" });
const ctl = { width: "100%", padding: "7px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, boxSizing: "border-box", fontFamily: "inherit" };

export default function ChangeFolderDialog({ turnover, graphToken, driveInfo, sectionFolderNames = [], onSaved, onClose }) {
  const entityFolder = entityBasePath(turnover.ContractingEntity);
  const year = String(turnover.JobNumber || "").split("-")[0];
  const [folders, setFolders] = useState(null);   // null = loading
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [link, setLink] = useState("");
  const [picked, setPicked] = useState(null);     // { id, name, webUrl }
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    if (!graphToken || !driveInfo) { setLoadError("SharePoint isn't connected yet — try again in a moment."); setFolders([]); return; }
    listJobFolders(graphToken, driveInfo.driveId, entityFolder, year)
      .then(f => alive && setFolders(f))
      .catch(e => { if (alive) { setLoadError(e.message); setFolders([]); } });
    return () => { alive = false; };
  }, [graphToken, driveInfo, entityFolder, year]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  async function useLink() {
    setError(""); setBusy("Checking the link…");
    try {
      const f = await resolveFolderLink(graphToken, driveInfo.driveId, link.trim(), shareIdForUrl(link.trim()));
      setPicked(f);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  async function save() {
    if (!picked) return;
    setError("");
    try {
      setBusy("Checking the Turnover folders…");
      const created = await ensureTurnoverSubfolders(graphToken, driveInfo.driveId, picked.id, sectionFolderNames);
      setBusy("Saving…");
      const res = await turnoverWrite(`EXEC sp_SetTurnoverFolder @payload = '${payloadOf(setFolderPayload(turnover.TurnoverID, picked))}'`);
      const out = res?.ResultSets?.Table1?.[0];
      if (!out || Number(out.Result) !== 1) throw new Error(out?.Error || "The folder was not saved.");
      onSaved({ SharePointFolderUrl: out.SharePointFolderUrl, ConstructionFolderItemId: out.ConstructionFolderItemId }, created);
    } catch (e) {
      setError(e.message);
      setBusy("");
    }
  }

  const ranked = rankJobFolders(folders || [], turnover.JobNumber, query);
  return (
    <>
      <div onClick={() => !busy && onClose()} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.35)", zIndex: 1000 }} />
      <div role="dialog" aria-label="Change the turnover folder" style={{ position: "fixed", top: "8vh", left: "50%", transform: "translateX(-50%)", width: "min(720px, 94vw)", maxHeight: "84vh", display: "flex", flexDirection: "column", background: "var(--surface)", borderRadius: 10, boxShadow: "0 12px 40px rgba(0,0,0,.25)", zIndex: 1001 }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cs-border)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>Change the turnover folder</div>
          <div style={{ fontSize: 12.5, color: "var(--text3)", marginTop: 3 }}>
            Pick the job folder this turnover's documents go into. Uploads from now on go into its Turnover folder; files already uploaded stay where they are.
          </div>
        </div>
        <div style={{ padding: "12px 18px", overflow: "auto", flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
            Existing folders in Construction - Documents › {entityFolder} › {year} Jobs
          </div>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search folders…" style={{ ...ctl, marginBottom: 8 }} aria-label="Search folders" />
          <div style={{ border: "1px solid var(--cs-border)", borderRadius: 6, maxHeight: 280, overflow: "auto" }}>
            {folders === null && <div style={{ padding: 12, fontSize: 13, color: "var(--text3)" }}>Loading folders…</div>}
            {loadError && <div style={{ padding: 12, fontSize: 13, color: "#b91c1c" }}>{loadError}</div>}
            {folders && !loadError && ranked.length === 0 && <div style={{ padding: 12, fontSize: 13, color: "var(--text3)" }}>No folders match.</div>}
            {ranked.map(f => {
              const on = picked?.id === f.id;
              return (
                <button key={f.id} type="button" onClick={() => setPicked({ id: f.id, name: f.name, webUrl: f.webUrl })}
                  style={{ display: "flex", width: "100%", gap: 8, alignItems: "center", padding: "7px 10px", border: "none", borderBottom: "1px solid var(--cs-border)", textAlign: "left", cursor: "pointer", fontSize: 13,
                    background: on ? "#dbeafe" : "transparent", color: "var(--text)" }}>
                  <span>📁</span><span style={{ flex: 1 }}>{f.name}</span>
                  {f.matchesJob && <span style={{ fontSize: 11, color: "#166534", fontWeight: 700 }}>this job number</span>}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", margin: "14px 0 6px" }}>Or paste a SharePoint folder link</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={link} onChange={e => setLink(e.target.value)} placeholder="https://…sharepoint.com/…" style={ctl} aria-label="Folder link" />
            <button type="button" style={btn(false)} disabled={!link.trim() || !!busy} onClick={useLink}>Use link</button>
          </div>
          {picked && <div style={{ marginTop: 12, fontSize: 13, color: "var(--text)" }}>Selected: <strong>{picked.name}</strong> — its Turnover folder and section folders are created if missing.</div>}
          {error && <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c" }}>{error}</div>}
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--cs-border)", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
          {busy && <span style={{ fontSize: 12.5, color: "var(--text3)", marginRight: "auto" }}>{busy}</span>}
          <button type="button" style={btn(false)} disabled={!!busy} onClick={onClose}>Cancel</button>
          <button type="button" style={{ ...btn(true), opacity: picked && !busy ? 1 : 0.5 }} disabled={!picked || !!busy} onClick={save}>Use this folder</button>
        </div>
      </div>
    </>
  );
}
